"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useMemo } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useWaveDropsLeaderboard, WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import MyStreamWaveMyVote from "./MyStreamWaveMyVote";
import { useLayout } from "../layout/LayoutContext";
import { WaveLeaderboardLoadingBar } from "@/components/waves/leaderboard/drops/WaveLeaderboardLoadingBar";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import MyStreamWaveMyVotesReset from "./MyStreamWaveMyVotesReset";
interface MyStreamWaveMyVotesProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const MyStreamWaveMyVotes: React.FC<MyStreamWaveMyVotesProps> = t0 => {
  const $ = _c(30);
  const {
    wave,
    onDropClick
  } = t0;
  const [pausePolling, setPausePolling] = useState(false);
  let t1;
  if ($[0] !== pausePolling || $[1] !== wave.id) {
    t1 = {
      waveId: wave.id,
      sort: WaveDropsLeaderboardSort.MY_REALTIME_VOTE,
      pausePolling
    };
    $[0] = pausePolling;
    $[1] = wave.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    drops,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage
  } = useWaveDropsLeaderboard(t1);
  const {
    myVotesViewStyle
  } = useLayout();
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [checkedDrops, setCheckedDrops] = useState(t2);
  let t3;
  if ($[4] !== checkedDrops || $[5] !== drops) {
    t3 = !!drops.length && drops.every(drop => checkedDrops.has(drop.id));
    $[4] = checkedDrops;
    $[5] = drops;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const allItemsSelected = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = dropId => {
      setCheckedDrops(prev => {
        if (prev.has(dropId)) {
          prev.delete(dropId);
        } else {
          prev.add(dropId);
        }
        return new Set(prev);
      });
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleToggleCheck = t4;
  let t5;
  if ($[8] !== allItemsSelected || $[9] !== drops) {
    t5 = () => {
      if (allItemsSelected) {
        setCheckedDrops(new Set());
      } else {
        setCheckedDrops(new Set(drops.map(_temp)));
      }
    };
    $[8] = allItemsSelected;
    $[9] = drops;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleToggleSelectAll = t5;
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = dropId_0 => {
      setCheckedDrops(prev_0 => {
        prev_0.delete(dropId_0);
        return new Set(prev_0);
      });
    };
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const removeSelected = t6;
  let t7;
  if ($[12] !== fetchNextPage || $[13] !== hasNextPage || $[14] !== isFetching || $[15] !== isFetchingNextPage) {
    t7 = async () => {
      if (hasNextPage && !isFetching && !isFetchingNextPage) {
        await fetchNextPage();
      }
    };
    $[12] = fetchNextPage;
    $[13] = hasNextPage;
    $[14] = isFetching;
    $[15] = isFetchingNextPage;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const intersectionElementRef = useIntersectionObserver(t7);
  let t8;
  if ($[17] !== allItemsSelected || $[18] !== checkedDrops || $[19] !== drops || $[20] !== handleToggleSelectAll || $[21] !== intersectionElementRef || $[22] !== isFetching || $[23] !== isFetchingNextPage || $[24] !== onDropClick || $[25] !== pausePolling) {
    t8 = drops.length === 0 && !isFetching ? <div className="tw-mt-10"><p className="tw-text-center tw-text-sm tw-text-iron-500">You haven't voted on any submissions in this wave yet.</p></div> : <div className="tw-mt-4 tw-space-y-4"><MyStreamWaveMyVotesReset haveDrops={!!drops.length} selected={checkedDrops} onToggleSelectAll={handleToggleSelectAll} allItemsSelected={allItemsSelected} removeSelected={removeSelected} setPausePolling={setPausePolling} /><div className="tw-space-y-2">{drops.map(drop_1 => <MyStreamWaveMyVote key={drop_1.id} drop={drop_1} onDropClick={onDropClick} isChecked={checkedDrops.has(drop_1.id)} onToggleCheck={handleToggleCheck} isResetting={pausePolling} />)}{isFetchingNextPage && <WaveLeaderboardLoadingBar />}<div ref={intersectionElementRef} /></div></div>;
    $[17] = allItemsSelected;
    $[18] = checkedDrops;
    $[19] = drops;
    $[20] = handleToggleSelectAll;
    $[21] = intersectionElementRef;
    $[22] = isFetching;
    $[23] = isFetchingNextPage;
    $[24] = onDropClick;
    $[25] = pausePolling;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== myVotesViewStyle || $[28] !== t8) {
    t9 = <div className="tw-space-y-4 tw-overflow-y-auto tw-px-2 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 hover:tw-scrollbar-thumb-iron-300 sm:tw-px-4 lg:tw-space-y-6" style={myVotesViewStyle}>{t8}</div>;
    $[27] = myVotesViewStyle;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
};
export default MyStreamWaveMyVotes;
function _temp(drop_0) {
  return drop_0.id;
}